import {
  format,
  formatDuration,
  formatDistanceToNow as fnsDistance,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { de, enUS, id, ja, ko, pl, ru, vi } from "date-fns/locale";
import Decimal from "decimal.js";
import i18next from "i18next";
import { registerLocale } from "react-datepicker";

// Should update this map when we support more languages
export const DATE_FNS_LOCALE_MAP: Record<string, Locale> = {
  en: enUS,
  vi: vi,
  ru: ru,
  pl: pl,
  id: id,
  tl: enUS,
  de: de,
  ja: ja,
  ko: ko,
};

export const formatDistanceToNow = (date: Date, lang: string) => {
  return fnsDistance(date, { locale: DATE_FNS_LOCALE_MAP[lang] ?? enUS });
};

export const formatDate = (date: Date, formatStr: string, lang: string) => {
  return format(date, formatStr, { locale: DATE_FNS_LOCALE_MAP[lang] ?? enUS });
};

export const formatDateApi = (utcDate: number | Date) => {
  return formatInTimeZone(utcDate, "UTC", "Y-MM-dd HH:mm:ss");
};

export const formatLocaleDuration = (duration: Duration, lang: string) => {
  return formatDuration(duration, {
    locale: DATE_FNS_LOCALE_MAP[lang] ?? enUS,
  });
};

export const registerDatePickerLocales = () => {
  Object.keys(DATE_FNS_LOCALE_MAP).forEach((localeName) => {
    registerLocale(localeName, DATE_FNS_LOCALE_MAP[localeName]);
  });
};

export function formatTokenAmount(
  amount: Decimal.Value,
  scale: number,
  code: string
): string {
  amount = __parseNumber(amount);

  const num = Intl.NumberFormat(i18next.language, {
    maximumFractionDigits: scale,
  }).format(amount);

  return `${num} ${code.toUpperCase()}`;
}

export function formatFiat(amount: Decimal.Value, currency: string): string {
  amount = __parseNumber(amount);
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  };
  const numberFmt = Intl.NumberFormat(i18next.language, options);
  const { maximumFractionDigits } = numberFmt.resolvedOptions();
  if (Math.abs(amount) < 1) {
    return __formatSmallFiatAmount(amount, options, maximumFractionDigits);
  }
  return numberFmt.format(__roundDownAmount(amount, maximumFractionDigits));
}

function __formatSmallFiatAmount(
  amount: number,
  options: Intl.NumberFormatOptions,
  fractionDigits: number
) {
  const absAmount = Math.abs(amount);
  if (absAmount < 0.0001) {
    fractionDigits = 8;
  } else if (absAmount < 0.01) {
    fractionDigits = 4;
  }
  return Intl.NumberFormat(i18next.language, {
    ...options,
    maximumFractionDigits: fractionDigits,
  }).format(__roundDownAmount(amount, fractionDigits));
}

function __roundDownAmount(amount: number, numDigits: number) {
  return new Decimal(amount)
    .toDecimalPlaces(numDigits, Decimal.ROUND_DOWN)
    .toNumber();
}

export function formatNumber(value: Decimal.Value, scale?: number): string {
  value = __parseNumber(value);

  return Intl.NumberFormat(i18next.language, {
    style: "decimal",
    maximumFractionDigits: scale ?? 2,
  }).format(value);
}

function __parseNumber(value: Decimal.Value): number {
  if (typeof value === "string") {
    return parseFloat(value);
  }
  if (value instanceof Decimal) {
    return value.toNumber();
  }
  return value;
}
